/**
 * Get Category Rules 驱动项（TK-PUB-25）
 * 接口：GET /product/202309/categories/{category_id}/rules
 *
 * 平台按类目下发：
 * - `product_certifications[]`：受限类目必传认证（含 `requirement_conditions` 条件必填、`expiration_date.is_required`）；
 * - `cod.is_supported`：不支持时不得传 `is_cod_allowed=true`，否则 listing 失败；
 * - `package_dimension.is_required`、`epr.is_required`、`responsible_person.is_required`、`manufacturer.is_required`；
 * - `fees[]`：PFAND 押金（挂在 `skus[].fees`）。
 *
 * 本模块负责：归一化规则 → 判定条件必填 → 装配 Create Product 载荷 → 提交前本地校验。
 * 只依赖纯函数，便于在装配器与校验器之间共享。
 */

/** Create Product：`fees[].type` 目前仅 PFAND */
export const TIKTOK_FEE_TYPE_PFAND = 'PFAND';

/** Create Product：`fees[].additional_attribute` 取值 */
export const TIKTOK_PFAND_ADDITIONAL_ATTRIBUTES = ['SINGLE_USE', 'REUSABLE', 'NOT_APPLICABLE'];

/** Create Product：`certifications` 最多 10 条 */
const CERTIFICATION_MAX = 10;

/** 认证图上传 use_case */
export const TIKTOK_CERTIFICATION_IMAGE_USE_CASE = 'CERTIFICATION_IMAGE';

/** 规则未加载时的占位（避免 undefined 判定） */
export const EMPTY_TIKTOK_CATEGORY_RULES = Object.freeze({
  loaded: false,
  raw: null,
  certifications: [],
  codSupported: false,
  eprRequired: false,
  responsiblePersonRequired: false,
  manufacturerRequired: false,
  packageDimensionRequired: false,
  fees: [],
  pfandRequired: false,
  /** Create Product `skus[].pre_sale` 仅在此非空时可用（缺失 = 无权限） */
  allowedSpecialProductTypes: [],
});

function asText(value) {
  return String(value ?? '').trim();
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asBool(value) {
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  return null;
}

/** 兼容 `{data}` / `{response:{data}}` / 裸 data 三种包装 */
export function resolveCategoryRulesData(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const direct = raw.data;
  if (direct && typeof direct === 'object' && !Array.isArray(direct)) return direct;
  const nested = raw?.response?.data;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) return nested;
  if (
    raw.product_certifications
    || raw.cod
    || raw.epr
    || raw.manufacturer
    || raw.responsible_person
    || raw.fees
    || raw.size_chart
    || raw.allowed_special_product_types
  ) {
    return raw;
  }
  return {};
}

/** 归一化 Get Category Rules 响应为前端可消费结构 */
export function normalizeTiktokCategoryRules(raw) {
  const data = resolveCategoryRulesData(raw);
  if (!Object.keys(data).length) return { ...EMPTY_TIKTOK_CATEGORY_RULES };

  const certifications = asArray(data.product_certifications)
    .map((row) => ({
      id: asText(row?.id),
      name: asText(row?.name) || asText(row?.id),
      isRequired: row?.is_required === true,
      documentDetails: asText(row?.document_details),
      sampleImageUrl: asText(row?.sample_image_url),
      expirationDateRequired: row?.expiration_date?.is_required === true,
      requirementConditions: asArray(row?.requirement_conditions)
        .map((cond) => ({
          conditionType: asText(cond?.condition_type) || 'VALUE_ID_MATCH',
          attributeId: asText(cond?.attribute_id),
          attributeValueId: asText(cond?.attribute_value_id),
        }))
        .filter((cond) => cond.attributeId && cond.attributeValueId),
    }))
    .filter((row) => row.id);

  const fees = asArray(data.fees)
    .map((row) => ({
      type: asText(row?.type).toUpperCase(),
      isRequired: row?.is_required === true,
    }))
    .filter((row) => row.type);

  return {
    loaded: true,
    raw: data,
    certifications,
    codSupported: data?.cod?.is_supported === true,
    eprRequired: data?.epr?.is_required === true,
    responsiblePersonRequired: data?.responsible_person?.is_required === true,
    manufacturerRequired: data?.manufacturer?.is_required === true,
    packageDimensionRequired: data?.package_dimension?.is_required === true,
    fees,
    pfandRequired: fees.some((row) => row.type === TIKTOK_FEE_TYPE_PFAND && row.isRequired),
    // 预售（skus[].pre_sale）：顶层字符串数组；「缺失」等同无权限，一律视为不支持
    allowedSpecialProductTypes: asArray(data?.allowed_special_product_types)
      .map((row) => asText(row).toUpperCase())
      .filter(Boolean),
  };
}

/** 收集某属性已填写的值 ID（用于 VALUE_ID_MATCH 条件判定） */
function collectAttrValueIds(productAttributes, attributeId) {
  const ids = new Set();
  const raw = productAttributes && typeof productAttributes === 'object'
    ? productAttributes[attributeId]
    : null;
  const push = (entry) => {
    if (entry == null) return;
    if (typeof entry === 'object') {
      const id = asText(
        entry.valueId ?? entry.attributeValueId ?? entry.id ?? entry.vid ?? entry.value_id,
      );
      if (id) ids.add(id);
      return;
    }
    const text = asText(entry);
    if (text) ids.add(text);
  };
  if (Array.isArray(raw)) raw.forEach(push);
  else push(raw);
  return ids;
}

/**
 * 判定每条认证当前是否必填：
 * - `is_required=true` → 始终必填；
 * - `is_required=false` → 命中任一 `requirement_conditions` 才必填。
 */
export function resolveCategoryCertifications(productAttributes, rules) {
  return asArray(rules?.certifications).map((cert) => {
    const conditions = asArray(cert.requirementConditions);
    const matched = conditions.filter((cond) => {
      if (cond.conditionType !== 'VALUE_ID_MATCH') return false;
      return collectAttrValueIds(productAttributes, cond.attributeId).has(cond.attributeValueId);
    });
    return {
      ...cert,
      required: cert.isRequired || matched.length > 0,
      conditionMatched: matched.length > 0,
    };
  });
}

function imageUri(image) {
  if (!image) return '';
  if (typeof image === 'string') return asText(image);
  return asText(image.uri || image.tiktokUri || image.tiktokUrl || image.url);
}

/** 'YYYY-MM-DD' / Date / 秒 / 毫秒 → unix 秒（UTC+0），非法返回 0 */
export function toUnixSeconds(raw) {
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw > 1e12 ? Math.floor(raw / 1000) : Math.floor(raw);
  }
  const text = asText(raw);
  if (!text) return 0;
  if (/^\d+$/.test(text)) return toUnixSeconds(Number(text));
  const parsed = Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T00:00:00Z` : text);
  return Number.isFinite(parsed) ? Math.floor(parsed / 1000) : 0;
}

/** 表单里已填的某条认证（按认证 ID 匹配） */
export function findCertificationFormValue(form, certificationId) {
  const id = asText(certificationId);
  if (!id) return null;
  return asArray(form?.certifications)
    .find((row) => asText(row?.certificationId ?? row?.id) === id) || null;
}

/** 认证是否已提供材料（至少 1 张图或 1 个 PDF 文件） */
export function isCertificationFilled(form, certificationId) {
  const row = findCertificationFormValue(form, certificationId);
  if (!row) return false;
  const hasImage = asArray(row.images).some((img) => imageUri(img));
  const hasFile = asArray(row.files).some((file) => asText(file?.id) && asText(file?.name));
  return hasImage || hasFile;
}

/** 装配 Create Product `certifications[]`（仅提交已填写的条目） */
export function buildTiktokCertificationsPayload(form, activeCertifications = []) {
  const activeIds = new Set(asArray(activeCertifications).map((cert) => asText(cert.id)));
  const out = [];
  asArray(form?.certifications).forEach((row) => {
    const id = asText(row?.certificationId ?? row?.id);
    if (!id) return;
    if (activeIds.size && !activeIds.has(id)) return;
    const images = asArray(row?.images)
      .map((img) => ({ uri: imageUri(img) }))
      .filter((img) => img.uri);
    const files = asArray(row?.files)
      .map((file) => ({
        id: asText(file?.id),
        name: asText(file?.name),
        format: asText(file?.format) || 'PDF',
      }))
      .filter((file) => file.id && file.name);
    if (!images.length && !files.length) return;
    const entry = { id };
    if (images.length) entry.images = images;
    if (files.length) entry.files = files;
    const expiration = toUnixSeconds(row?.expirationDate);
    if (expiration > 0) entry.expiration_date = expiration;
    out.push(entry);
  });
  return out.slice(0, CERTIFICATION_MAX);
}

/**
 * 装配 `is_cod_allowed`：类目不支持 COD 时一律不传（传 true 会导致 listing 失败）。
 */
export function buildTiktokIsCodAllowed(form, rules) {
  if (!rules?.codSupported) return undefined;
  const value = asBool(form?.isCodAllowed);
  return value === null ? undefined : value;
}

/** 归一化 ID 列表（制造商 / 欧代） */
export function normalizeTiktokComplianceIds(raw) {
  const list = Array.isArray(raw) ? raw : (raw == null || raw === '' ? [] : [raw]);
  const seen = new Set();
  return list
    .map((item) => asText(typeof item === 'object' ? (item?.id ?? item?.value) : item))
    .filter((id) => {
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
}

function moneyAmount(raw) {
  if (raw == null || raw === '') return '';
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return '';
  return n.toFixed(2);
}

/** 装配 `skus[].fees`：类目要求 PFAND 且已填金额时返回条目，否则 null */
export function buildTiktokSkuFees(form, rules) {
  if (!rules?.pfandRequired) return null;
  const amount = moneyAmount(form?.pfandAmount);
  if (!amount) return null;
  const entry = { type: TIKTOK_FEE_TYPE_PFAND, amount };
  const additional = asText(form?.pfandType).toUpperCase();
  if (TIKTOK_PFAND_ADDITIONAL_ATTRIBUTES.includes(additional)) {
    entry.additional_attribute = additional;
  }
  return [entry];
}

/**
 * 提交前本地校验类目规则项。
 * 规则未加载时一律放行（平台 listing_check / Create Product 会给出准确报错），避免拉不到规则就发不出品。
 * @returns {null | { field: string, section: string, message: string }}
 */
export function validateTiktokCategoryCompliance(form, rules) {
  if (!rules?.loaded) return null;
  const active = resolveCategoryCertifications(form?.productAttributes, rules);

  const missing = active.filter((cert) => cert.required && !isCertificationFilled(form, cert.id));
  if (missing.length) {
    return {
      field: 'certifications',
      section: 'compliance',
      message: `请上传类目要求的资质认证：${missing.map((cert) => cert.name).join('、')}`,
    };
  }

  const missingExpiration = active.filter((cert) => {
    if (!cert.required || !cert.expirationDateRequired) return false;
    const row = findCertificationFormValue(form, cert.id);
    return toUnixSeconds(row?.expirationDate) <= 0;
  });
  if (missingExpiration.length) {
    return {
      field: 'certifications',
      section: 'compliance',
      message: `请填写认证有效期：${missingExpiration.map((cert) => cert.name).join('、')}`,
    };
  }

  // 制造商 / 欧盟责任人：官方 EU 流程为选填（虚线框），站内不强制；是否展示由市场 region 控制

  if (rules.pfandRequired && !buildTiktokSkuFees(form, rules)) {
    return {
      field: 'pfandAmount',
      section: 'compliance',
      message: '当前类目要求填写押金（PFAND）金额',
    };
  }

  return null;
}
